import type { AiOperationalPlan } from '../../../types/aiOperational'

export interface AiExecutionPreview {
  allowed: boolean
  preambleKind: 'CONFIDENT' | 'APOLOGY'
  previewText: string
}

const affectedScope = (plan: AiOperationalPlan) => {
  const targets = plan.envelope.simulation?.affectedRecords
    ?? plan.envelope.operations.flatMap((operation) => operation.targetRefs)
  if (!targets.length) return 'Affected scope: no persistent records declared.'
  return `Affected scope: ${targets.slice(0, 4).map((target) => target.title || target.id).join(', ')}${targets.length > 4 ? ` +${targets.length - 4}` : ''}.`
}

const intelligenceLines = (plan: AiOperationalPlan) => {
  const preview = plan.envelope.simulation?.intelligencePreview
  if (!preview) return []
  return [
    `${preview.title}: ${preview.summary}`,
    `Manage path: ${preview.reviewPath}`,
    `Constraints: ${preview.constraints.slice(0, 2).join(' ')}`,
  ]
}

const forecastLines = (plan: AiOperationalPlan) => {
  const forecasts = plan.envelope.simulation?.forecasts ?? plan.envelope.contextDigest?.forecasts ?? []
  if (!forecasts.length) return []
  return [
    `Forecast: ${forecasts.slice(0, 3).map((forecast) => `${forecast.severity.toLowerCase()} ${forecast.kind.toLowerCase().replace(/_/g, ' ')}`).join(', ')}.`,
    ...forecasts.slice(0, 2).map((forecast) => `Recommended: ${forecast.recommendedAction}`),
  ]
}

const executionPlanLines = (plan: AiOperationalPlan) => {
  const executionPlan = plan.envelope.executionPlan
  if (!executionPlan) return []
  const visibleSteps = executionPlan.steps.slice(0, 4)
  const executionGraph = plan.envelope.executionGraph
  return [
    `Plan: ${executionPlan.summary}`,
    ...(executionGraph ? [
      `Graph: ${executionGraph.state.toLowerCase().replace(/_/g, ' ')}, ${executionGraph.steps.length} steps, ${executionGraph.dependencies.length} dependencies.`,
    ] : []),
    `Checkpoints: ${executionPlan.checkpoints.length ? executionPlan.checkpoints.map((checkpoint) => checkpoint.title).slice(0, 3).join('; ') : 'none required for local guidance'}.`,
    ...visibleSteps.map((step) => `Step: ${step.title} (${step.authority.toLowerCase().replace(/_/g, ' ')}).`),
  ]
}

export const buildExecutionPreview = (plan: AiOperationalPlan): AiExecutionPreview => {
  const risk = plan.envelope.risk.toLowerCase()
  const review = plan.envelope.reviewRequired ? 'Manage approval required for persistent changes' : 'no persistent mutation planned'
  const reversible = plan.envelope.operations.some((operation) => operation.rollback?.reversible) ? 'rollback metadata available' : 'rollback not needed for this preview'
  const summary = plan.envelope.simulation?.summary ?? plan.envelope.audit.summary

  if (plan.envelope.risk === 'RESTRICTED' || plan.nextStep === 'REFUSE') {
    return {
      allowed: false,
      preambleKind: 'APOLOGY',
      previewText: [
        'Execution preview: restricted operation.',
        plan.envelope.audit.summary,
        'I cannot generate an execution walkthrough for destructive, admin, economy, permission, integration, or automation-activation actions from AI Magic.',
      ].join('\n'),
    }
  }

  return {
    allowed: true,
    preambleKind: plan.envelope.risk === 'LOW' ? 'CONFIDENT' : 'APOLOGY',
    previewText: [
      `Execution preview: ${risk} risk, ${review}, ${reversible}.`,
      affectedScope(plan),
      summary,
      ...intelligenceLines(plan),
      ...forecastLines(plan),
      ...executionPlanLines(plan),
      plan.envelope.risk === 'LOW'
        ? 'This journey is guidance-only and may start as a safe UI walkthrough.'
        : 'This journey can guide Manage approval preparation only; it will not execute workspace mutations directly.',
    ].join('\n'),
  }
}
